import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
  redirectTo?: string;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ 
  children, 
  allowedRoles, 
  redirectTo = '/dashboard' 
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  const hasRequiredRole = user.role && allowedRoles.includes(user.role);

  if (!hasRequiredRole) {
    // For drivers trying to access restricted pages, show access denied page
    if (user.role === 'driver') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              {t('access.denied')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('access.driverRestriction')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('access.allowedPages')}: {t('menu.dashboard')}, {t('menu.drivers')}
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.history.back()}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.href = '/dashboard'}
            >
              {t('access.goToDashboard')}
            </Button>
          </Box>
        </Box>
      );
    }

    // For other unauthorized users, redirect
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default RoleBasedRoute;
